type DatedPost = {
  createdAt?: unknown
  rawPost?: Record<string, unknown> | null
}

const MIN_PLAUSIBLE_MS = Date.UTC(1995, 0, 1)
const ONE_DAY_MS = 24 * 60 * 60 * 1000

/** Raw-post fields sources use for "when was this posted", most reliable first. `change` comes last on purpose: on
 * Gelbooru-style sites it is a unix timestamp, but on Moebooru sites (Sakugabooru) it is a revision counter, so it is
 * only trusted when the value looks like a real timestamp. */
const RAW_DATE_FIELDS = ["created_at", "createdAt", "created", "posted", "date", "uploaded_at", "upload_date", "published", "change"]

/** Reads one date value in any of the shapes the sources use: an ISO string, a unix number in seconds or milliseconds,
 * or the "Mon Oct 05 09:01:40 -0500 2026" form Gelbooru sends. Returns milliseconds, or undefined if it isn't a
 * plausible date (before 1995 or more than a day in the future). */
export function parseDateValue(value: unknown, now: number = Date.now()): number | undefined {
  let ms: number | undefined
  if (typeof value === "number" && Number.isFinite(value)) {
    ms = value < 1e11 ? value * 1000 : value
  } else if (typeof value === "string" && value.trim()) {
    const text = value.trim()
    if (/^\d+(\.\d+)?$/.test(text)) {
      const n = Number(text)
      ms = n < 1e11 ? n * 1000 : n
    } else {
      const parsed = Date.parse(text)
      ms = Number.isNaN(parsed) ? undefined : parsed
    }
  }
  if (ms === undefined || ms < MIN_PLAUSIBLE_MS || ms > now + ONE_DAY_MS) return undefined
  return ms
}

/** When the post was made, in milliseconds, or undefined if the source doesn't say. */
export function getPostTimestamp(post: DatedPost, now: number = Date.now()): number | undefined {
  const direct = parseDateValue(post.createdAt, now)
  if (direct !== undefined) return direct
  const raw = post.rawPost
  if (!raw || typeof raw !== "object") return undefined
  for (const field of RAW_DATE_FIELDS) {
    const found = parseDateValue(raw[field], now)
    if (found !== undefined) return found
  }
  return undefined
}

export interface PostDateRange {
  from: Date
  to: Date
  /** Keep posts whose source gives no date too (they cannot be placed in the range, so by default they are left out). */
  includeUndated?: boolean
}

/** Whether a post falls inside the chosen range. A post with no usable date can't be placed, so it is left out while
 * a range is active (otherwise the filter would silently do nothing for that source), unless the range says includeUndated. */
export function postInDateRange(post: DatedPost, range: PostDateRange | undefined | null, now: number = Date.now()): boolean {
  if (!range) return true
  const timestamp = getPostTimestamp(post, now)
  if (timestamp === undefined) return range.includeUndated === true
  return timestamp >= range.from.getTime() && timestamp <= range.to.getTime()
}
